import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createProduct, getProduct, updateProduct } from '@/services/productsService';
import { ClothingItemFormData, ClothingItem, ShoeItem } from '@/models/types';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { UploadCloudIcon, X, Image as ImageIcon, Clock, User, ExternalLink, Tag, Loader2, AlertTriangle } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Badge } from '@/components/ui/badge';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { categories, getCategoryDisplayName, getFlattenedCategoryOptions } from '@/lib/categories';

const defaultFormData: ClothingItemFormData = {
  name: '',
  brand: '',
  price: 0,
  link: '',
  "category[0]": '',
  "category[1]": '',
  "category[2]": '',
  batch: '',
  image: null,
  created_by: ''
};

const batchOptions = [
  { label: 'None', value: 'none' },
  { label: 'Top Batch', value: 'Top Batch' },
  { label: 'Mid Batch', value: 'Mid Batch' },
  { label: 'Low Batch', value: 'Low Batch' }
];

// Flatten categories for backwards compatibility
const categoryOptions = getFlattenedCategoryOptions();

const ItemForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<ClothingItemFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [item, setItem] = useState<ClothingItem | ShoeItem | null>(null);
  const [creationInfo, setCreationInfo] = useState<{
    createdBy: string;
    createdAt: string;
    updatedAt: string | null;
  } | null>(null);

  const [brands, setBrands] = useState<string[]>([]);
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const brandInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [convertingLink, setConvertingLink] = useState(false);
  const [originalLink, setOriginalLink] = useState('');
  const [linkConversionError, setLinkConversionError] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const { data: brandsData, error: brandsError } = await supabase
          .from('products')
          .select('brand')
          .not('brand', 'is', null);
        
        if (brandsError) throw brandsError;
        
        if (brandsData) {
          const uniqueBrands = Array.from(new Set(
            brandsData.map(item => item.brand).filter(Boolean)
          )).sort();
          
          setBrands(uniqueBrands as string[]);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        toast.error('Failed to load brand suggestions');
      }
    };
    
    fetchBrands();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      const fetchItem = async () => {
        try {
          setLoading(true);
          const itemData = await getProduct(parseInt(id));
          setItem(itemData);
          setFormData({
            name: itemData.name,
            brand: itemData.brand,
            price: itemData.price,
            link: itemData.link,
            "category[0]": itemData["category[0]"],
            "category[1]": itemData["category[1]"],
            "category[2]": itemData["category[2]"],
            batch: itemData.batch,
            image: null,
            created_by: itemData.created_by || ''
          });
          
          const categoryList = [
            itemData["category[0]"], 
            itemData["category[1]"], 
            itemData["category[2]"]
          ].filter(Boolean);
          
          setSelectedCategories(categoryList);
          
          // Set main category and subcategory if they exist
          if (itemData["category[0]"]) {
            // Check if the category is a main category
            const isMainCategory = categories.some(cat => cat.name === itemData["category[0]"]);
            
            if (isMainCategory) {
              setSelectedMainCategory(itemData["category[0]"]);
              
              // Check if category[1] is a subcategory of category[0]
              if (itemData["category[1]"]) {
                const mainCat = categories.find(cat => cat.name === itemData["category[0]"]);
                if (mainCat && mainCat.subcategories.includes(itemData["category[1]"])) {
                  setSelectedSubcategory(itemData["category[1]"]);
                }
              }
            } else {
              // Try to find which main category contains this as a subcategory
              for (const category of categories) {
                if (category.subcategories.includes(itemData["category[0]"])) {
                  setSelectedMainCategory(category.name);
                  setSelectedSubcategory(itemData["category[0]"]);
                  break;
                }
              }
            }
          }
          
          if (itemData.created_by) {
            const now = new Date().toISOString();
            setCreationInfo({
              createdBy: itemData.created_by,
              createdAt: itemData.created_at || now,
              updatedAt: itemData.updated_at || null
            });
          }
          
          setImagePreview(itemData.image);
        } catch (error) {
          console.error('Error fetching item:', error);
          toast.error('Failed to load item details');
          navigate('/admin/items');
        } finally {
          setLoading(false);
        }
      };

      fetchItem();
    } else if (!isEditMode && user) {
      setFormData(prev => ({
        ...prev,
        created_by: user.email || 'Unknown user'
      }));
    }
  }, [id, isEditMode, navigate, user]);

  useEffect(() => {
    if (formData.brand) {
      const filtered = brands.filter(brand => 
        brand.toLowerCase().includes(formData.brand.toLowerCase())
      );
      setFilteredBrands(filtered);
    } else {
      setFilteredBrands(brands);
    }
  }, [formData.brand, brands]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'price') {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0,
      });
    } else if (name === 'brand') {
      setFormData({
        ...formData,
        [name]: value,
      });
      setShowBrandSuggestions(true);
    } else if (name === 'link') {
      setFormData({
        ...formData,
        [name]: value,
      });
      setOriginalLink(value);
      setLinkConversionError(false);
      
      if (value && value.trim() !== '' && !value.includes('affiliate.replibrary.com')) {
        const timeoutId = setTimeout(() => {
          convertAffiliateLink();
        }, 1000);
        
        return () => clearTimeout(timeoutId);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleMainCategoryChange = (value: string) => {
    setSelectedMainCategory(value);
    setSelectedSubcategory('');
    
    // Update selected categories list
    const updatedCategories = [...selectedCategories];
    if (!updatedCategories.includes(value)) {
      if (updatedCategories.length >= 3) {
        updatedCategories.pop(); // Remove last category if already have 3
      }
      updatedCategories.unshift(value); // Add main category as first item
    }
    
    setSelectedCategories(updatedCategories);
    
    // Update form data while preserving image
    setFormData(prev => ({
      ...prev,
      "category[0]": updatedCategories[0] || '',
      "category[1]": updatedCategories[1] || '',
      "category[2]": updatedCategories[2] || '',
    }));
  };
  
  const handleSubcategoryChange = (value: string) => {
    setSelectedSubcategory(value);
    
    // Update selected categories list
    const updatedCategories = [...selectedCategories];
    const mainCategoryIndex = updatedCategories.indexOf(selectedMainCategory);
    
    // If main category exists, add subcategory after it, otherwise just add to the list
    if (mainCategoryIndex !== -1) {
      // Remove any existing items after main category
      updatedCategories.splice(mainCategoryIndex + 1);
      // Add subcategory if it fits
      if (updatedCategories.length < 3) {
        updatedCategories.push(value);
      }
    } else if (updatedCategories.length < 3) {
      updatedCategories.push(value);
    } else {
      updatedCategories.pop(); // Remove last category if already have 3
      updatedCategories.push(value);
    }
    
    setSelectedCategories(updatedCategories);
    
    // Update form data while preserving image
    setFormData(prev => ({
      ...prev,
      "category[0]": updatedCategories[0] || '',
      "category[1]": updatedCategories[1] || '',
      "category[2]": updatedCategories[2] || '',
    }));
  };

  const handleCategoryToggle = (category: string) => {
    let updatedCategories: string[];
    
    if (selectedCategories.includes(category)) {
      updatedCategories = selectedCategories.filter(c => c !== category);
      
      // If we're removing a main category, also reset the main category state
      if (category === selectedMainCategory) {
        setSelectedMainCategory('');
        setSelectedSubcategory('');
      }
    } else {
      if (selectedCategories.length >= 3) {
        toast.error('You can select up to 3 categories');
        return;
      }
      
      updatedCategories = [...selectedCategories, category];
    }
    
    setSelectedCategories(updatedCategories);
    
    // Update form data while preserving image
    setFormData(prev => ({
      ...prev,
      "category[0]": updatedCategories[0] || '',
      "category[1]": updatedCategories[1] || '',
      "category[2]": updatedCategories[2] || '',
    }));
  };

  const handleBrandSelect = (brand: string) => {
    setFormData({
      ...formData,
      brand: brand,
    });
    setShowBrandSuggestions(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    if (imagePreview) {
      if (imagePreview !== item?.image) {
        URL.revokeObjectURL(imagePreview);
      }
      
      setImagePreview(null);
      setFormData({
        ...formData,
        image: null,
      });
    }
  };

  const validateForm = () => {
    const errors: string[] = [];
    
    if (!formData.name || formData.name.trim() === '') {
      errors.push('Name is required');
    }
    
    if (formData.price <= 0) {
      errors.push('Price must be greater than 0');
    }
    
    if (!formData["category[0]"]) {
      errors.push('Primary category is required');
    }
    
    if (!isEditMode && !formData.image) {
      errors.push('Image is required for new items');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }
    
    if (formData.link && 
        !formData.link.includes('affiliate.replibrary.com') && 
        !linkConversionError && 
        !convertingLink) {
      await convertAffiliateLink();
    }

    try {
      setSaving(true);
      const formDataToSubmit = new FormData();
      
      // Create a copy of formData with the correct image value
      const submissionData = { ...formData };
      
      // If we're in edit mode and no new image was uploaded, use the existing image URL
      if (isEditMode && !formData.image && item?.image) {
        submissionData.image = item.image;
      }
      
      Object.entries(submissionData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (value instanceof File) {
            formDataToSubmit.append(key, value);
          } else if (typeof value === 'number') {
            formDataToSubmit.append(key, value.toString());
          } else {
            formDataToSubmit.append(key, value as string);
          }
        }
      });

      if (formData.image instanceof File) {
        formDataToSubmit.append('image', formData.image);
      }

      if (formData.link) {
        formDataToSubmit.set('link', formData.link);
      }

      let savedItem;
      if (id) {
        savedItem = await updateProduct(parseInt(id), submissionData as any);
      } else {
        savedItem = await createProduct(submissionData);
      }

      toast.success(`Item ${id ? 'updated' : 'created'} successfully`);
      navigate('/admin/clothinglist');
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const convertAffiliateLink = async () => {
    if (!formData.link || formData.link.trim() === '') {
      return;
    }

    if (formData.link.includes('affiliate.replibrary.com')) {
      return;
    }

    try {
      setConvertingLink(true);
      setLinkConversionError(false);
      
      const response = await fetch(`https://affiliate.replibrary.com/convert?link=${encodeURIComponent(formData.link)}`);
      
      if (!response.ok) {
        throw new Error('Failed to convert link');
      }
      
      const responseData = await response.json();
      let kakobuyLink = responseData.kakobuy || '';
      
      if (kakobuyLink && !kakobuyLink.startsWith('http://') && !kakobuyLink.startsWith('https://')) {
        kakobuyLink = `https://${kakobuyLink}`;
      }
      
      if (kakobuyLink) {
        setFormData(prev => ({
          ...prev,
          link: kakobuyLink
        }));
      } else {
        throw new Error('No kakobuy link in response');
      }
    } catch (error) {
      console.error('Error converting link:', error);
      setLinkConversionError(true);
    } finally {
      setConvertingLink(false);
    }
  };

  const handleTabChange = async (value: string) => {
    if (formData.link && 
        !formData.link.includes('affiliate.replibrary.com') && 
        !linkConversionError) {
      await convertAffiliateLink();
    }
    
    setActiveTab(value);
  };

  const cardPreview = useMemo(() => {
    const displayPrice = formData.price ? `$${formData.price.toFixed(2)}` : '$0.00';
    const displayCategory = formData["category[0]"] || 'N/A';
    const displayQuality = formData.batch || '';
    
    return {
      name: formData.name || 'Product Name',
      price: displayPrice,
      category: displayCategory,
      quality: displayQuality,
      image: imagePreview,
      link: formData.link || '#',
      originalLink: originalLink
    };
  }, [formData, imagePreview, originalLink]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          <span className="text-sm text-muted-foreground">Loading item details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/admin/clothinglist')}
          className="mr-2"
        >
          <X className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isEditMode ? 'Edit Item' : 'Add New Item'}</h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update item details and images' : 'Add a new item to your inventory'}
          </p>
        </div>
      </div>

      {isEditMode && creationInfo && (
        <Card className="bg-background/50 border border-border/50 shadow-subtle">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Created by:</span>
                <span className="font-medium">{creationInfo.createdBy}</span>
              </div>
              
              {creationInfo.createdAt && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Created:</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(creationInfo.createdAt), { addSuffix: true })}
                  </span>
                </div>
              )}
              
              {creationInfo.updatedAt && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Last updated:</span>
                  <span className="font-medium">
                    {formatDistanceToNow(new Date(creationInfo.updatedAt), { addSuffix: true })}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Tabs 
          defaultValue="details" 
          value={activeTab} 
          onValueChange={handleTabChange}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Item Details</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Enter the basic details about the item</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Product name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="price">Price ($)</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label htmlFor="brand">Brand</Label>
                    <Input
                      id="brand"
                      name="brand"
                      placeholder="Enter brand name"
                      value={formData.brand}
                      onChange={handleInputChange}
                      onFocus={() => setShowBrandSuggestions(true)}
                      onBlur={() => {
                        setTimeout(() => setShowBrandSuggestions(false), 200);
                      }}
                      ref={brandInputRef}
                    />
                    {showBrandSuggestions && filteredBrands.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-background border border-border rounded-md shadow-md max-h-48 overflow-y-auto">
                        {filteredBrands.map((brand, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-accent cursor-pointer"
                            onClick={() => handleBrandSelect(brand)}
                          >
                            {brand}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="link">Product Link</Label>
                    <Input
                      id="link"
                      name="link"
                      placeholder="https://example.com/product"
                      value={formData.link}
                      onChange={handleInputChange}
                      className="flex-1"
                    />
                    {formData.link !== originalLink && originalLink && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Original: <span className="italic truncate inline-block max-w-xs">{originalLink}</span>
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        Categories <span className="text-muted-foreground text-xs">(Select up to 3)</span>
                      </Label>
                      {selectedCategories.length > 0 && (
                        <Badge variant="outline" className="font-normal">
                          {selectedCategories.length}/3 selected
                        </Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mainCategory">Main Category</Label>
                        <Select
                          value={selectedMainCategory}
                          onValueChange={handleMainCategoryChange}
                        >
                          <SelectTrigger id="mainCategory">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.name} value={category.name}>
                                {getCategoryDisplayName(category.name)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedMainCategory && (
                        <div className="space-y-2">
                          <Label htmlFor="subcategory">Subcategory</Label>
                          <Select
                            value={selectedSubcategory}
                            onValueChange={handleSubcategoryChange}
                            disabled={!categories.find(cat => cat.name === selectedMainCategory)?.subcategories.length}
                          >
                            <SelectTrigger id="subcategory">
                              <SelectValue placeholder={
                                categories.find(cat => cat.name === selectedMainCategory)?.subcategories.length
                                  ? "Select subcategory"
                                  : "No subcategories available"
                              } />
                            </SelectTrigger>
                            <SelectContent>
                              {categories
                                .find(cat => cat.name === selectedMainCategory)?.subcategories
                                .map((subcat) => (
                                  <SelectItem key={subcat} value={subcat}>
                                    {getCategoryDisplayName(subcat)}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    
                    {selectedCategories.length > 0 && (
                      <div className="mt-4">
                        <Label>Selected Categories</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedCategories.map((category) => (
                            <Badge 
                              key={category}
                              variant="secondary"
                              className="flex items-center gap-1"
                            >
                              {getCategoryDisplayName(category)}
                              <Button 
                                type="button" 
                                size="icon" 
                                variant="ghost" 
                                className="h-4 w-4 p-0 hover:bg-transparent" 
                                onClick={() => handleCategoryToggle(category)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="batch">Batch</Label>
                  <Select
                    value={formData.batch || 'none'}
                    onValueChange={(value) => handleSelectChange("batch", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select batch quality" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border border-border/50">
                      {batchOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/admin/items')}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={() => handleTabChange('images')}
                  disabled={convertingLink}
                >
                  Next: Upload Images
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="images" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>Item Image</CardTitle>
                <CardDescription>
                  Upload an image for this item.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-center w-full">
                  <Label
                    htmlFor="image-upload"
                    className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-background/50 hover:bg-background/80 border-border/50 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <UploadCloudIcon className="w-10 h-10 mb-3 text-muted-foreground" />
                      <p className="mb-2 text-sm text-muted-foreground">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG or GIF (Max size: 5MB)
                      </p>
                    </div>
                    <Input
                      id="image-upload"
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Label>
                </div>
                
                {imagePreview ? (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Uploaded Image</h3>
                    <div className="relative group max-w-md mx-auto">
                      <div className="aspect-square rounded-md overflow-hidden border border-border/50 bg-background/50">
                        <img
                          src={imagePreview}
                          alt="Product preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-md p-8 text-center border-border/50">
                    <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground" />
                    <h3 className="mt-2 text-sm font-medium">No image uploaded</h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {isEditMode 
                        ? "Upload a new image or keep the existing one"
                        : "Upload an image for this item"}
                    </p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => handleTabChange('details')}>
                  Back
                </Button>
                <Button type="button" onClick={() => handleTabChange('preview')}>
                  Next: Preview Item
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4">
            <Card className="bg-background border border-border/50 shadow-subtle">
              <CardHeader>
                <CardTitle>Item Preview</CardTitle>
                <CardDescription>
                  See how your item will appear in the catalog
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-w-xs mx-auto">
                  <div className="group cursor-pointer relative">
                    <div className="relative overflow-hidden rounded-lg aspect-[14/16] bg-white">
                      {cardPreview.image ? (
                        <img 
                          alt={cardPreview.name} 
                          className="w-full h-full object-cover transform transition-transform duration-500"
                          src={cardPreview.image}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.svg';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <ImageIcon className="h-16 w-16 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex flex-col justify-end pointer-events-none">
                        <div className="absolute bottom-0 left-0 right-0 h-[45%] md:h-[35%] md:group-hover:h-[45%] transition-all duration-300 bg-gradient-to-t from-[rgba(0,0,0,0.85)] via-[rgba(0,0,0,0.5)] to-transparent"></div>
                        <div className="relative">
                          <div className="p-2 sm:p-4 transform translate-y-0 transition-transform duration-300 group-hover:-translate-y-12">
                            <div className="flex items-start justify-between gap-2 sm:gap-3">
                              <h3 className="text-white text-xs sm:text-sm md:text-base font-medium leading-tight flex-1 drop-shadow truncate">
                                {cardPreview.name}
                              </h3>
                            </div>
                            <div className="flex items-center justify-between text-xs sm:text-sm mt-1">
                              <div className="text-white/90 truncate max-w-[40%]">
                                {cardPreview.category}
                              </div>
                              <div className="flex items-center gap-1 sm:gap-3">
                                <div className="text-[#ff9797]">
                                  {cardPreview.quality === 'none' ? '' : cardPreview.quality}
                                </div>
                                <div className="text-white font-medium">
                                  {cardPreview.price}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 px-2 sm:px-4 pb-2 sm:pb-4 opacity-0 translate-y-full group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 pointer-events-auto">
                            <div className="flex gap-2">
                              <a 
                                href={cardPreview.link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="flex-1 bg-white text-black py-1 sm:py-2 rounded text-xs sm:text-sm font-medium text-center hover:bg-gray-100 transition-colors"
                              >
                                Check Out
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <h3 className="text-lg font-semibold">Preview Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Name:</span>
                        <span className="font-medium">{cardPreview.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Brand:</span>
                        <span className="font-medium">{formData.brand || 'Not specified'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="font-medium">{cardPreview.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Categories:</span>
                        <span className="font-medium text-right">
                          {selectedCategories.length > 0 
                            ? selectedCategories.join(', ') 
                            : 'Not specified'}
                        </span>
                      </div>
                      {cardPreview.quality && cardPreview.quality !== 'none' && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Batch Quality:</span>
                          <span className="font-medium">{cardPreview.quality}</span>
                        </div>
                      )}
                      {cardPreview.link && cardPreview.link !== '#' && (
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              {linkConversionError ? 'Link:' : 'Affiliate Link:'}
                            </span>
                            <a 
                              href={cardPreview.link} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline flex items-center gap-1"
                            >
                              <span className="max-w-[200px] truncate">
                                {cardPreview.link.replace(/^https?:\/\//, '')}
                              </span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          {cardPreview.originalLink && cardPreview.originalLink !== cardPreview.link && (
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-muted-foreground">Original:</span>
                              <span className="italic text-muted-foreground max-w-[200px] truncate">
                                {cardPreview.originalLink.replace(/^https?:\/\//, '')}
                              </span>
                            </div>
                          )}
                          {linkConversionError && (
                            <div className="flex items-center gap-1 mt-1 text-yellow-600 dark:text-yellow-500 text-xs">
                              <AlertTriangle className="h-3 w-3" />
                              <span>Note: Affiliate conversion failed. Using original link.</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button type="button" variant="outline" onClick={() => handleTabChange('images')}>
                  Back
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="h-4 w-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditMode ? 'Update Item' : 'Create Item'
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
};

export default ItemForm;
